import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, addDoc, onSnapshot, query, 
  where, doc, updateDoc, getDoc, Timestamp, orderBy, deleteDoc 
} from 'firebase/firestore';
import Swal from 'sweetalert2';

function Vendoraccoount() {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [payments, setPayments] = useState([]);
  const [vendorData, setVendorData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Modal & Form State
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [amount, setAmount] = useState("");
  const [payMode, setPayMode] = useState("Cash");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const userId = auth.currentUser?.uid;
  const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });

  // 1. Fetch Vendors
  useEffect(() => {
    if (!userId) return;
    const qV = query(collection(db, "vendors"), where("userId", "==", userId));
    const unsub = onSnapshot(qV, (snap) => setVendors(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [userId]);

  // 2. Fetch Payments for Selected Vendor
  useEffect(() => {
    if (!selectedVendor) {
      setPayments([]);
      setVendorData(null);
      return;
    }
    const vData = vendors.find(v => v.id === selectedVendor);
    setVendorData(vData);

    const qP = query(
      collection(db, "vendor_payments"), 
      where("vendorId", "==", selectedVendor),
      orderBy("createdAt", "desc")
    );
    
    const unsub = onSnapshot(qP, (snap) => {
      setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [selectedVendor, vendors]);

  const openModal = (payment = null) => {
    if (payment) {
      setEditId(payment.id);
      setAmount(payment.amount);
      setPayMode(payment.payMode);
      setDescription(payment.description);
      setDate(payment.date);
    } else {
      setEditId(null);
      setAmount("");
      setPayMode("Cash");
      setDescription("");
      setDate(new Date().toISOString().split('T')[0]);
    }
    setShowModal(true);
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payAmt = parseFloat(amount);
      const vRef = doc(db, "vendors", selectedVendor);

      if (editId) {
        const oldPayment = payments.find(p => p.id === editId);
        const diff = payAmt - oldPayment.amount;

        await updateDoc(doc(db, "vendor_payments", editId), {
          amount: payAmt, payMode, description, date, updatedAt: Timestamp.now()
        });

        const vSnap = await getDoc(vRef);
        await updateDoc(vRef, { currentBalance: (vSnap.data().currentBalance || 0) - diff });
        Toast.fire({ icon: 'success', title: 'Updated' });
      } else {
        await addDoc(collection(db, "vendor_payments"), {
          userId, vendorId: selectedVendor, amount: payAmt, payMode, description, date, createdAt: Timestamp.now()
        });

        const vSnap = await getDoc(vRef);
        await updateDoc(vRef, { currentBalance: (vSnap.data().currentBalance || 0) - payAmt });
        Toast.fire({ icon: 'success', title: 'Payment Saved' });
      }
      setShowModal(false);
    } catch (err) {
      Toast.fire({ icon: 'error', title: err.message });
    } finally { setLoading(false); }
  };

  const deletePayment = async (payment) => {
    const confirm = await Swal.fire({
      title: 'Delete Payment?',
      text: "Balance will be added back to vendor account.",
      icon: 'warning',
      showCancelButton: true,
    });

    if (confirm.isConfirmed) {
      const vRef = doc(db, "vendors", selectedVendor);
      const vSnap = await getDoc(vRef);
      await updateDoc(vRef, { currentBalance: (vSnap.data().currentBalance || 0) + payment.amount });
      await deleteDoc(doc(db, "vendor_payments", payment.id));
      Toast.fire({ icon: 'success', title: 'Deleted' });
    }
  };

  return (
    <div className="content-wrapper bg-light min-vh-100">
      <section className="content-header py-3 px-4">
        <div className="row align-items-center bg-white p-3 shadow-sm rounded border-left-success">
          <div className="col-md-6"><h4 className="m-0 font-weight-bold text-navy">Vendor Ledger</h4></div>
          <div className="col-md-6">
            <select className="form-control" value={selectedVendor} onChange={(e) => setSelectedVendor(e.target.value)}>
              <option value="">-- Choose Vendor --</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
        </div>
      </section>

      <section className="content px-4">
        {vendorData ? (
          <>
            <div className="row mb-4">
              <div className="col-md-4">
                <div className="card shadow-sm border-0 p-3 bg-white">
                  <small className="text-muted font-weight-bold">OPENING BALANCE</small>
                  <h3 className="mb-0">₹ {vendorData.openingBalance?.toLocaleString()}</h3>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card shadow-sm border-0 p-3 bg-white border-left-danger">
                  <small className="text-danger font-weight-bold">REMAINING TO PAY</small>
                  <h3 className="mb-0 text-danger">₹ {vendorData.currentBalance?.toLocaleString()}</h3>
                </div>
              </div>
              <div className="col-md-4 d-flex align-items-center">
                <button className="btn btn-success btn-lg btn-block shadow" onClick={() => openModal()}>
                  <i className="fas fa-plus-circle mr-2"></i> Add Payment
                </button>
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white"><h6 className="m-0 font-weight-bold">Transaction History</h6></div>
              <div className="card-body p-0">
                <table className="table table-hover mb-0">
                  <thead className="bg-light">
                    <tr><th className="text-center">Sr.</th><th>Date</th><th>Mode</th><th>Note</th><th className="text-right">Amount</th><th className="text-center">Action</th></tr>
                  </thead>
                  <tbody>
                    {payments.map((p, i) => (
                      <tr key={p.id}>
                        <td className="text-center">{i + 1}</td>
                        <td>{p.date}</td>
                        <td><span className="badge badge-secondary">{p.payMode}</span></td>
                        <td>{p.description || '--'}</td>
                        <td className="text-right font-weight-bold text-success">₹ {p.amount.toLocaleString()}</td>
                        <td className="text-center">
                          <button className="btn btn-sm text-info mr-2" onClick={() => openModal(p)}><i className="fas fa-edit"></i></button>
                          <button className="btn btn-sm text-danger" onClick={() => deletePayment(p)}><i className="fas fa-trash"></i></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-5 text-muted"><h5>Select a vendor to start</h5></div>
        )}
      </section>

      {/* REACT CONTROLLED MODAL */}
      {showModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal-content animate__animated animate__zoomIn">
            <div className="modal-header bg-navy text-white">
              <h5 className="modal-title">{editId ? 'Edit Payment' : 'New Payment'}</h5>
              <button className="close text-white" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handlePayment}>
              <div className="p-4">
                <div className="row mb-3">
                  <div className="col-6"><label className="small font-weight-bold">Date</label><input type="date" className="form-control" value={date} onChange={e => setDate(e.target.value)} required /></div>
                  <div className="col-6"><label className="small font-weight-bold">Mode</label>
                    <select className="form-control" value={payMode} onChange={e => setPayMode(e.target.value)}>
                      <option>Cash</option><option>UPI</option><option>Bank</option><option>Cheque</option>
                    </select>
                  </div>
                </div>
                <label className="small font-weight-bold">Amount (₹)</label>
                <input type="number" className="form-control form-control-lg text-success mb-3" value={amount} onChange={e => setAmount(e.target.value)} required />
                <label className="small font-weight-bold">Note</label>
                <textarea className="form-control" value={description} onChange={e => setDescription(e.target.value)}></textarea>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>Close</button>
                <button type="submit" disabled={loading} className="btn btn-navy">{loading ? 'Processing...' : 'Save Payment'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .bg-navy { background: #001f3f; }
        .btn-navy { background: #001f3f; color: white; }
        .custom-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;
        }
        .custom-modal-content {
          background: white; width: 500px; border-radius: 8px; overflow: hidden; box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        }
        .border-left-danger { border-left: 5px solid #dc3545; }
        .border-left-success { border-left: 5px solid #28a745; }
      `}} />
    </div>
  );
}

export default Vendoraccoount;